import React, { useState } from 'react';

// PackageCard — collapsible card representing a VAT or Luxembourg batch
// in the Pending Approval lane. Visually distinct from individual invoice
// cards: hue-tinted left rail, mono "VAT PACKAGE" / "LUX PACKAGE" tag,
// segmented checklist progress bar, and an "Approve Package" action that
// lights up once the checklist is 100% complete.
//
// Design tokens are kept local (matching what KanbanBoard.js exports
// inline) so the component can be dropped into the existing custom-CSS
// stack without pulling in a shared theme module.

const TOKENS = {
  bg:           'oklch(0.985 0.003 250)',
  panel:        'oklch(1 0 0)',
  panelAlt:     'oklch(0.975 0.004 250)',
  border:       'oklch(0.92 0.006 250)',
  borderStrong: 'oklch(0.86 0.008 250)',
  ink:          'oklch(0.18 0.012 260)',
  ink2:         'oklch(0.42 0.012 260)',
  ink3:         'oklch(0.62 0.010 255)',
  ok:           'oklch(0.62 0.13 150)',
  danger:       'oklch(0.58 0.18 25)',
};

// Repointed to the shared Inter stack (src/typography.css). MONO_FONT is
// kept because the name is load-bearing across this file's inline styles.
const UI_FONT = 'var(--font-ui)';
const MONO_FONT = 'var(--font-ui)';

// Inline styles win over CSS classes, so amounts/identifiers spread these
// instead of using the .num/.code utility classes. Identifiers (invoice
// numbers) additionally get the slashed zero.
const NUM_STYLE = {
  fontFamily: 'var(--font-ui)',
  fontVariantNumeric: 'tabular-nums',
};
const CODE_STYLE = {
  ...NUM_STYLE,
  fontFeatureSettings: '"zero" 1',
};

const laneColor = (hue, l = 0.62, c = 0.14) => `oklch(${l} ${c} ${hue})`;
const laneSoft = (hue) => `oklch(0.96 0.03 ${hue})`;

// Hue per package kind — matches the design study (VAT=290 purple,
// Luxembourg=35 amber). The Pending Approval lane itself uses 40 (golden)
// but the package cards follow the *path* hue, not the lane hue.
const HUE = { vat: 290, luxembourg: 35 };

function formatMoney(amountStr) {
  if (amountStr == null || amountStr === '') return '';
  const num = parseFloat(amountStr);
  if (Number.isNaN(num)) return '';
  return num.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function ChecklistSegment({ items }) {
  // Segmented progress bar, one segment per checklist item. Uses the
  // package's hue so the visual reads as "this is the VAT path" or
  // "this is the Luxembourg path" at a glance.
  if (!items || !items.length) return null;
  return (
    <div style={{ display: 'flex', gap: 3 }}>
      {items.map((it) => (
        <div
          key={it.id}
          title={`${it.completed ? '✓ ' : ''}${it.label}`}
          style={{
            flex: 1,
            height: 5,
            borderRadius: 2,
            background: it.completed
              ? TOKENS.ok
              : TOKENS.border,
            transition: 'background .15s',
          }}
        />
      ))}
    </div>
  );
}

function ChildInvoiceRow({ child, onClick }) {
  return (
    <div
      onClick={(e) => {
        e.stopPropagation();
        onClick && onClick(child);
      }}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '5px 8px 5px 10px',
        borderTop: `1px dashed ${TOKENS.border}`,
        cursor: onClick ? 'pointer' : 'default',
        fontFamily: UI_FONT,
        fontSize: 11,
        color: TOKENS.ink2,
        background: 'transparent',
      }}
    >
      <span
        style={{
          ...CODE_STYLE,
          fontSize: 9.5,
          color: TOKENS.ink3,
          minWidth: 60,
        }}
      >
        {child.invoice_number || `#${(child.id || '').slice(0, 6)}`}
      </span>
      <span
        style={{
          flex: 1,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          color: TOKENS.ink,
          fontWeight: 500,
        }}
      >
        {child.vendor_name || 'Unknown vendor'}
      </span>
      <span
        style={{
          ...NUM_STYLE,
          fontSize: 10.5,
          color: TOKENS.ink2,
        }}
      >
        {child.currency || ''} {formatMoney(child.amount)}
      </span>
    </div>
  );
}

function ChecklistInline({ items, hue, busyId, onToggle }) {
  // Inline checkbox list shown inside the expanded card. Each row is a
  // toggle that fires onToggle(itemId, nextValue). Optimistic state lives
  // in the parent so a rejected API call rolls the bar back.
  if (!items || !items.length) {
    return (
      <div
        style={{
          fontSize: 11,
          color: TOKENS.ink3,
          fontStyle: 'italic',
          padding: '6px 10px',
        }}
      >
        No checklist defined for this package.
      </div>
    );
  }
  return (
    <ul
      style={{
        listStyle: 'none',
        margin: 0,
        padding: '4px 4px 6px',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      {items.map((it) => {
        const busy = busyId === it.id;
        return (
          <li key={it.id} style={{ margin: 0 }}>
            <label
              onClick={(e) => e.stopPropagation()}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '5px 8px',
                borderRadius: 5,
                cursor: busy ? 'wait' : 'pointer',
                background: 'transparent',
                fontFamily: UI_FONT,
                fontSize: 11.5,
                color: it.completed ? TOKENS.ink2 : TOKENS.ink,
                opacity: busy ? 0.6 : 1,
              }}
            >
              <input
                type="checkbox"
                checked={!!it.completed}
                disabled={busy}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => {
                  e.stopPropagation();
                  onToggle(it.id, !it.completed);
                }}
                style={{
                  accentColor: laneColor(hue),
                  width: 14,
                  height: 14,
                  cursor: busy ? 'wait' : 'pointer',
                  flex: '0 0 auto',
                }}
              />
              <span
                style={{
                  // Use the shorthand `textDecoration` line so React doesn't
                  // warn about mixing it with `textDecorationColor`.
                  textDecoration: it.completed
                    ? `line-through ${TOKENS.ink3}`
                    : 'none',
                  flex: 1,
                }}
              >
                {it.label}
              </span>
            </label>
          </li>
        );
      })}
    </ul>
  );
}

function ChecklistReadOnly({ items, hue }) {
  // Static read-only checklist shown when the package card renders in
  // mode='approved'. Same vertical rhythm as ChecklistInline so swapping
  // them doesn't shift the layout. No checkbox input, no hover affordance,
  // no cursor change — a custom check / empty indicator communicates state
  // without inviting interaction.
  if (!items || !items.length) {
    return (
      <div
        style={{
          fontSize: 11,
          color: TOKENS.ink3,
          fontStyle: 'italic',
          padding: '6px 10px',
        }}
      >
        No checklist defined for this package.
      </div>
    );
  }
  return (
    <ul
      style={{
        listStyle: 'none',
        margin: 0,
        padding: '4px 4px 6px',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      {items.map((it) => (
        <li key={it.id} style={{ margin: 0 }}>
          <div
            // Stop propagation so clicks on a checklist row don't collapse
            // the parent card — matches ChecklistInline's behavior.
            onClick={(e) => e.stopPropagation()}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '5px 8px',
              borderRadius: 5,
              cursor: 'default',
              background: 'transparent',
              fontFamily: UI_FONT,
              fontSize: 11.5,
              color: it.completed ? TOKENS.ink2 : TOKENS.ink,
            }}
          >
            <span
              aria-hidden="true"
              style={{
                width: 14,
                height: 14,
                flex: '0 0 auto',
                borderRadius: 3,
                border: `1.5px solid ${
                  it.completed ? laneColor(hue) : TOKENS.borderStrong
                }`,
                background: it.completed ? laneColor(hue) : 'transparent',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
              }}
            >
              {it.completed && (
                <svg
                  width="9"
                  height="9"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M5 12l4 4 10-10" />
                </svg>
              )}
            </span>
            <span
              style={{
                textDecoration: it.completed
                  ? `line-through ${TOKENS.ink3}`
                  : 'none',
                flex: 1,
              }}
            >
              {it.label}
            </span>
          </div>
        </li>
      ))}
    </ul>
  );
}

function PackageCard({
  package: pkg,
  kind, // 'vat' | 'luxembourg'
  mode = 'pending', // 'pending' | 'approved'
  childInvoices, // array of card-shaped invoice objects (from flat list)
  onToggleChecklistItem, // (itemId, nextValue) => Promise — parent handles API + rollback
  onApprove, // (batchId) => Promise — parent handles API + UI refresh (pending mode only)
  onChildClick, // (invoice) => void — opens invoice detail when child row clicked
}) {
  const [expanded, setExpanded] = useState(false);
  const [busyItemId, setBusyItemId] = useState(null);
  const [approving, setApproving] = useState(false);
  const [errorText, setErrorText] = useState(null);

  const hue = HUE[kind] ?? 290;
  const checklist = pkg.checklist || { items: [], items_total: 0, items_completed: 0 };
  const items = Array.isArray(checklist.items) ? checklist.items : [];
  const itemsTotal = checklist.items_total ?? items.length;
  const itemsDone = checklist.items_completed ?? items.filter((i) => i.completed).length;
  const allComplete = itemsTotal > 0 && itemsDone >= itemsTotal;

  const headerLabel = kind === 'vat' ? 'VAT PACKAGE' : 'LUX PACKAGE';
  const periodLine =
    kind === 'vat'
      ? `${pkg.vendor || 'Vendor'} · ${pkg.period_label || pkg.period || ''}`
      : `${pkg.quarter_label || pkg.quarter || ''}${
          pkg.vendor_count ? ` · ${pkg.vendor_count} vendor${pkg.vendor_count === 1 ? '' : 's'}` : ''
        }`;

  const totalLocal = pkg.total_amount_local;
  const currency = pkg.currency || '';
  const childCount = pkg.invoice_count ?? (pkg.child_invoice_ids || []).length;

  const handleToggleItem = async (itemId, nextValue) => {
    if (!onToggleChecklistItem) return;
    setErrorText(null);
    setBusyItemId(itemId);
    try {
      await onToggleChecklistItem(itemId, nextValue);
    } catch (err) {
      const detail =
        err?.response?.data?.detail || err?.message || 'Failed to update checklist';
      setErrorText(detail);
    } finally {
      setBusyItemId(null);
    }
  };

  const handleApprove = async (e) => {
    e.stopPropagation();
    if (!onApprove || approving || !allComplete) return;
    setErrorText(null);
    setApproving(true);
    try {
      await onApprove(pkg.id);
    } catch (err) {
      const detail =
        err?.response?.data?.detail || err?.message || 'Approve failed';
      setErrorText(detail);
    } finally {
      setApproving(false);
    }
  };

  return (
    <div
      onClick={() => setExpanded((v) => !v)}
      style={{
        width: '100%',
        cursor: 'pointer',
        position: 'relative',
        background: TOKENS.panel,
        // Use longhand only — mixing `border` (shorthand) with `borderLeft`
        // triggers a React shorthand-vs-longhand warning on rerender.
        borderTop: `1px solid ${expanded ? laneColor(hue, 0.7, 0.12) : TOKENS.border}`,
        borderRight: `1px solid ${expanded ? laneColor(hue, 0.7, 0.12) : TOKENS.border}`,
        borderBottom: `1px solid ${expanded ? laneColor(hue, 0.7, 0.12) : TOKENS.border}`,
        borderLeft: `3px solid ${laneColor(hue)}`,
        borderRadius: 8,
        marginTop: 6,
        // Stacked-paper edges along the top to signal "this is a stack"
        boxShadow: expanded
          ? `0 0 0 1px ${laneColor(hue, 0.86, 0.06)}`
          : `0 -2px 0 -1px ${laneColor(hue, 0.92, 0.04)}, 0 -2px 0 0px ${laneColor(hue, 0.84, 0.06)}, 0 -4px 0 -1px ${laneColor(hue, 0.94, 0.03)}, 0 -4px 0 0px ${laneColor(hue, 0.88, 0.05)}`,
        fontFamily: UI_FONT,
        color: TOKENS.ink,
        transition: 'border-color .12s, box-shadow .12s',
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: '9px 11px 7px',
          display: 'flex',
          flexDirection: 'column',
          gap: 5,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span
            style={{
              fontFamily: MONO_FONT,
              fontSize: 9,
              fontWeight: 600,
              color: laneColor(hue, 0.38, 0.12),
              background: laneSoft(hue),
              border: `1px solid ${laneColor(hue, 0.86, 0.07)}`,
              padding: '1px 6px',
              borderRadius: 3,
              letterSpacing: '0.06em',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
            }}
          >
            <svg
              width="9"
              height="9"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M3 8l9 4 9-4-9-4-9 4zM3 12l9 4 9-4M3 16l9 4 9-4" />
            </svg>
            {headerLabel}
          </span>
          <span
            style={{
              marginLeft: 'auto',
              ...NUM_STYLE,
              fontSize: 10,
              color: TOKENS.ink2,
              background: TOKENS.panelAlt,
              border: `1px solid ${TOKENS.border}`,
              padding: '0 6px',
              borderRadius: 8,
            }}
          >
            {childCount} invoice{childCount === 1 ? '' : 's'}
          </span>
          <span
            aria-hidden="true"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 16,
              height: 16,
              color: TOKENS.ink3,
              transition: 'transform .15s',
              transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)',
            }}
          >
            <svg
              width="11"
              height="11"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M6 9l6 6 6-6" />
            </svg>
          </span>
        </div>

        <div
          style={{
            fontSize: 12.5,
            letterSpacing: '-0.005em',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontWeight: 600,
          }}
        >
          {periodLine}
        </div>

        <div style={{ display: 'flex', alignItems: 'baseline', gap: 7 }}>
          <span
            style={{
              fontFamily: MONO_FONT,
              fontSize: 9.5,
              color: TOKENS.ink3,
              letterSpacing: '0.04em',
            }}
          >
            {currency}
          </span>
          <span
            style={{
              ...NUM_STYLE,
              fontSize: 14,
              fontWeight: 500,
              letterSpacing: '-0.01em',
            }}
          >
            {formatMoney(totalLocal) || '—'}
          </span>
          {pkg.age_days != null && (
            <span
              style={{
                marginLeft: 'auto',
                fontSize: 10.5,
                color: TOKENS.ink3,
                ...NUM_STYLE,
              }}
            >
              {pkg.age_days}d open
            </span>
          )}
        </div>

        {/* Segmented checklist progress bar (always visible, even when collapsed) */}
        <div style={{ marginTop: 4 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              marginBottom: 3,
            }}
          >
            <span
              style={{
                fontFamily: MONO_FONT,
                fontSize: 9,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: laneColor(hue, 0.38, 0.12),
              }}
            >
              Checklist
            </span>
            <span
              style={{
                marginLeft: 'auto',
                ...NUM_STYLE,
                fontSize: 9.5,
                color: TOKENS.ink3,
              }}
            >
              {itemsDone}/{itemsTotal}
            </span>
          </div>
          <ChecklistSegment items={items} />
        </div>
      </div>

      {/* Expanded body */}
      {expanded && (
        <div
          style={{
            background: laneSoft(hue),
            borderTop: `1px solid ${laneColor(hue, 0.86, 0.06)}`,
            borderBottomLeftRadius: 5,
            borderBottomRightRadius: 7,
          }}
        >
          {/* Inline checklist — interactive in pending mode, read-only in
              approved mode (rendered by the conditional below). */}
          <div
            style={{
              padding: '6px 10px 0',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              fontFamily: MONO_FONT,
              fontSize: 9.5,
              color: laneColor(hue, 0.38, 0.12),
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
            }}
          >
            Checklist
            <span
              style={{
                marginLeft: 'auto',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {itemsDone}/{itemsTotal}
            </span>
          </div>
          <div style={{ padding: '0 6px 4px' }}>
            {mode === 'approved' ? (
              <ChecklistReadOnly items={items} hue={hue} />
            ) : (
              <ChecklistInline
                items={items}
                hue={hue}
                busyId={busyItemId}
                onToggle={handleToggleItem}
              />
            )}
          </div>

          {errorText && (
            <div
              style={{
                margin: '0 10px 6px',
                padding: '6px 8px',
                borderRadius: 5,
                background: 'oklch(0.95 0.04 25)',
                border: `1px solid ${TOKENS.danger}`,
                color: TOKENS.danger,
                fontSize: 11,
                fontFamily: UI_FONT,
              }}
            >
              {errorText}
            </div>
          )}

          {/* Approve Package action (pending mode) — replaced by a static
              status line in approved mode. The actual downstream actions
              (wire request, signed Cat B form, mark Sent to AP) live on the
              dedicated VAT / Luxembourg dashboards. */}
          {mode === 'approved' ? (
            <div
              style={{
                padding: '6px 10px 8px',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                fontFamily: UI_FONT,
                fontSize: 11,
                color: laneColor(hue, 0.32, 0.12),
              }}
            >
              <svg
                width="11"
                height="11"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.4"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M5 12l4 4 10-10" />
              </svg>
              <span style={{ fontWeight: 600 }}>Approved</span>
              <span style={{ color: TOKENS.ink3 }}>
                {kind === 'vat'
                  ? '— awaiting wire request'
                  : '— awaiting Cat B form'}
              </span>
            </div>
          ) : (
            <div
              style={{
                padding: '6px 10px 8px',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <button
                type="button"
                onClick={handleApprove}
                disabled={!allComplete || approving}
                title={
                  allComplete
                    ? kind === 'luxembourg'
                      ? `Send all ${childCount} invoice${childCount === 1 ? '' : 's'} in this package to AP`
                      : `Approve all ${childCount} invoice${childCount === 1 ? '' : 's'} in this package`
                    : `Complete the checklist (${itemsDone}/${itemsTotal}) to enable`
                }
                style={{
                  padding: '7px 12px',
                  borderRadius: 6,
                  border: 'none',
                  background: allComplete
                    ? laneColor(hue, 0.5, 0.16)
                    : TOKENS.panelAlt,
                  color: allComplete ? '#fff' : TOKENS.ink3,
                  fontFamily: UI_FONT,
                  fontSize: 11.5,
                  fontWeight: 600,
                  cursor: !allComplete
                    ? 'not-allowed'
                    : approving
                      ? 'wait'
                      : 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  opacity: approving ? 0.7 : 1,
                  transition: 'background .12s, color .12s',
                }}
              >
                {approving ? (
                  <span
                    aria-hidden="true"
                    style={{
                      width: 11,
                      height: 11,
                      borderRadius: '50%',
                      border: '2px solid rgba(255,255,255,0.3)',
                      borderTopColor: '#fff',
                      animation: 'erbi-spin 0.9s linear infinite',
                    }}
                  />
                ) : (
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M5 12l4 4 10-10" />
                  </svg>
                )}
                {kind === 'luxembourg'
                  ? approving
                    ? 'Sending…'
                    : 'Send to AP'
                  : approving
                    ? 'Approving…'
                    : 'Approve Package'}
              </button>
              {!allComplete && (
                <span
                  style={{
                    fontSize: 10.5,
                    color: TOKENS.ink3,
                    fontFamily: UI_FONT,
                  }}
                >
                  Complete the checklist to enable.
                </span>
              )}
            </div>
          )}

          {/* Children rows */}
          <div
            style={{
              padding: '5px 10px',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              fontFamily: MONO_FONT,
              fontSize: 9.5,
              color: laneColor(hue, 0.38, 0.12),
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              borderTop: `1px solid ${laneColor(hue, 0.88, 0.05)}`,
            }}
          >
            Invoices in package
            <span
              style={{
                marginLeft: 'auto',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {childInvoices?.length ?? childCount}
            </span>
          </div>
          <div
            style={{
              background: TOKENS.panel,
              borderTop: `1px solid ${laneColor(hue, 0.88, 0.05)}`,
              borderBottomLeftRadius: 5,
              borderBottomRightRadius: 5,
            }}
          >
            {childInvoices && childInvoices.length > 0 ? (
              childInvoices.map((c) => (
                <ChildInvoiceRow key={c.id} child={c} onClick={onChildClick} />
              ))
            ) : (
              <div
                style={{
                  padding: '8px 10px',
                  fontSize: 11,
                  color: TOKENS.ink3,
                  fontStyle: 'italic',
                }}
              >
                No invoice details available.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default PackageCard;
